/** @jsxImportSource @emotion/react */

import { MissionManagerAccessMode } from "@a_team/models/dist/MissionObject";
import { Icon, Typography } from "@a_team/ui-components";
import { css } from "@emotion/react";
import { CollaboratorCard } from "components/Collaborators/CollaboratorTable/CollaboratorCard";
import { CollaboratorRows } from "components/Collaborators/CollaboratorTable/CollaboratorRows";
import { TableCellHeader } from "components/Collaborators/CollaboratorTable/TableCellHeader";
import LoadingFallback from "components/LoadingFallback";
import { Flags } from "configs/featureFlags";
import { Role, missionReadRoles, workspaceReadRoles } from "configs/role";
import { getMissionSpecificRoles, getPrimaryRole } from "helpers/access";
import useCanAccessMission from "hooks/access/useCanAccessMission";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useMinBreakpoint } from "hooks/useMinBreakpoint";
import { isEqual } from "lodash";
import { AccountMember, AccountRoleAccessLevel } from "models/Account";
import { EmailSettings, MissionManagerPayload } from "models/Mission";
import MissionSpec from "models/MissionSpec";
import { useAccountCollaborators } from "queries/accounts/collaborators/useAccountCollaborators";
import { useUpdateMemberAttrs } from "queries/accounts/collaborators/useUpdateMemberAttrs";
import { useUpdateMissionManagerSettings } from "queries/missions/useUpdateMissionManagerSettings";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useRootStore } from "store";
import { observer } from "mobx-react";
import { isBillingContact } from "views/utils";

interface CollaboratorsTableProps {
  ignoreManagers?: boolean; // If true, will not evaluate mission managers in the table from the current mission in the mission spec store
  closeCollaboratorManageModal?: () => void;
}

interface MissionSetting {
  userId: string;
  teamPulse: boolean;
  invoices: boolean;
  builderFeedback: boolean;
  mainManager: boolean;
  missionUpdates: boolean;
}

export interface CollaboratorState {
  userId: string;
  teamPulse: boolean;
  invoices: boolean;
  builderFeedback: boolean;
  mainManager: boolean;
  missionUpdates: boolean;
}

export interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

const DEFAULT_SORT_CONFIG: SortConfig = { key: "role", direction: "desc" };

const getCSSRules: CSSRulesResolver<{ cols: number }> = ({ colors, cols }) => ({
  container: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    rowGap: 12,
    width: "100%",
  },
  cards: {
    paddingTop: 24,
  },
  table: {
    width: "100%",
    display: "grid",
    gridTemplateColumns:
      cols > 3
        ? `240px 150px ${Array.from({ length: cols - 3 }, () => "auto").join(
            " "
          )} 80px`
        : Array.from({ length: cols }, () => "auto").join(" "),

    margin: "20px auto",
  },
  tableHeaderCell: {
    background: colors.Grey[100],
    borderTop: "none",
    height: 26,
  },
  tableCell: {
    padding: 12,
    borderTop: `1px solid ${colors.Grey[300]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  managerWarning: {
    background: colors.Red[200],
    padding: 12,
    borderRadius: 4,
    margin: "20px auto 0",
    width: "calc(100% - 24px)",
    p: {
      fontStyle: "italic",
    },
    "p:nth-child(1)": {
      fontWeight: 600,
      fontStyle: "normal",
    },
  },
});

const CollaboratorsTable: FC<CollaboratorsTableProps> = (props) => {
  const { flagEnabled: clientRolesAndPermissionsEnabled } = useFeatureFlag(
    Flags.ClientRolesAndPerms
  );

  // Ref to hold the previous collaborators
  const prevCollaborators = useRef();

  const {
    userStore: { user: currentUser },
    accountsStore: { currentAccountId },
    missionSpecStore,
  } = useRootStore();

  const {
    isLoading: loading,
    isLoadingError: error,
    data: collaborators,
  } = useAccountCollaborators(currentAccountId);

  const missionSpec = missionSpecStore?.missionSpec;
  const mission = missionSpec?.mission;
  const mid = mission?.mid || missionSpec?.platformId;
  const managers = props.ignoreManagers ? [] : mission?.managers || [];
  const { mutate: updateMemberAttributes } = useUpdateMemberAttrs();
  const hasWritePerms = useCanAccessMission("write");
  const showEmailSettings = !!mission;
  const showAdminSettings =
    !!mission && clientRolesAndPermissionsEnabled
      ? hasWritePerms
      : !!currentUser?.isAdmin;
  const [readyToSave, setReadyToSave] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT_CONFIG);

  const numColumns = useMemo(() => {
    let cols = 3;
    if (showEmailSettings) {
      cols += 1;
    }

    if (showAdminSettings && !clientRolesAndPermissionsEnabled) {
      cols += 3;
    }

    if (showAdminSettings && clientRolesAndPermissionsEnabled) {
      cols += 1;
    }

    return cols;
  }, [showAdminSettings, showEmailSettings, currentUser]);

  const styles = useCSSRulesWithTheme(getCSSRules, {
    cols: numColumns,
  });
  const tableHeaderStyles = css(styles.tableCell, styles.tableHeaderCell);
  const centeredTableHeader = css(styles.tableCell, styles.tableHeaderCell, {
    justifyContent: "center",
  });

  // Combines mission.managers with account.members and mainManagerUsername
  // to normalize true and false values for each collaborator - teamPulse, invoices, mainManager, missionUpdates
  const getFormState = (args: {
    mission?: MissionSpec["mission"];
    collaborators?: AccountMember[];
  }): CollaboratorState[] => {
    const { mission, collaborators } = args;
    const mainManagerId = managers.find(
      (m) => m?.username === mission?.mainManagerUsername
    )?.user?.uid;

    if (!collaborators) {
      return [];
    }

    return collaborators
      .map((c: AccountMember) => {
        const userId = c?.user?.id;
        const matchingManager = mission?.managers?.find(
          (manager) => manager?.user?.uid === userId
        );

        // If the user is not a manager (ex: BD Team Advisors) - default opt out of email settings except for Builder Feedback emails
        // For managers - undefined settings also opt out of email settings
        return {
          userId,
          teamPulse: matchingManager
            ? !(matchingManager.excludeFromTeamPulseEmails ?? false)
            : false,
          invoices: matchingManager
            ? !(matchingManager.excludeFromInvoiceEmails ?? false)
            : false,
          builderFeedback: matchingManager
            ? !(matchingManager.excludeFromBuilderFeedbackEmails ?? false)
            : false,
          missionUpdates: matchingManager
            ? !(matchingManager.excludeFromMissionUpdatesEmails ?? false)
            : false,
          mainManager: userId === (mainManagerId || ""),
        };
      })
      .filter(Boolean);
  };

  const getPreviousFormState = () => {
    const prevCollaboratorsArray = prevCollaborators.current as any;
    if (!prevCollaboratorsArray) {
      return [];
    }

    return getFormState({
      mission: missionSpecStore?.missionSpec?.mission,
      collaborators: prevCollaboratorsArray,
    });
  };

  const [formState, setFormState] = useState<MissionSetting[]>(
    getFormState({
      mission: missionSpecStore?.missionSpec?.mission,
      collaborators,
    })
  );

  const didFormStateChange = useMemo(() => {
    const prevCollaboratorsArray = prevCollaborators.current as any;
    if (!prevCollaboratorsArray) {
      return false;
    }

    const prevFormState = getPreviousFormState();

    return !isEqual(prevFormState, formState);
  }, [
    formState,
    missionSpecStore?.missionSpec?.mission,
    prevCollaborators.current,
  ]);

  const updateMissionSettings = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    const userId = e.target.dataset.userId;
    const attr = e.target.dataset.attr as string;
    const changingMainManager = attr === "mainManager";

    const defaultFormStateEntry = {
      userId,
      teamPulse: false,
      invoices: false,
      builderFeedback: false,
      mainManager: false,
      missionUpdates: false,
    };

    const newFormState = [...formState];
    const formStateIdx = newFormState.findIndex((s) => s.userId === userId);

    const formStateEntry = {
      ...(newFormState[formStateIdx] || defaultFormStateEntry),
      [attr]: value,
    };

    if (formStateIdx === -1) {
      newFormState.push(formStateEntry);
    } else {
      newFormState[formStateIdx] = formStateEntry;
    }

    if (changingMainManager) {
      newFormState.forEach((s) => {
        if (s.userId !== userId) {
          s.mainManager = false;
        }
      });
    }

    !readyToSave && setReadyToSave(true);
    setFormState(newFormState);
  };

  // Triggered when API fails to save mission settings
  const restorePreviousCollaborators = () => {
    const prevCollaboratorsArray = prevCollaborators.current as any;
    if (prevCollaboratorsArray) {
      setReadyToSave(false);
      setFormState(
        getFormState({
          mission: missionSpecStore?.missionSpec?.mission,
          collaborators: prevCollaboratorsArray,
        })
      );
    }
  };

  const { mutate: saveMissionManager, isLoading: saving } =
    useUpdateMissionManagerSettings(
      undefined,
      restorePreviousCollaborators,
      true
    );

  const saveMissionSettings = async (payload: MissionManagerPayload) => {
    if (!mid) {
      return;
    }

    saveMissionManager({ mid, payload });
  };

  // Saves the previous collaborators so we can restore later if needed
  useEffect(() => {
    prevCollaborators.current = collaborators as any;
    setFormState(
      getFormState({
        mission: missionSpecStore?.missionSpec?.mission,
        collaborators,
      })
    );
  }, [collaborators, missionSpecStore?.missionSpec?.mission]);

  // Listens for form changes and saves the mission automatically
  useEffect(() => {
    if (!loading && collaborators && readyToSave && didFormStateChange) {
      const prevFormState = getPreviousFormState();
      const updatedManager = formState.find((state) => {
        const prev = prevFormState.find((p) => p.userId === state.userId);
        return !isEqual(prev, state);
      });

      // No changes to save
      if (!updatedManager) {
        return;
      }

      saveMissionSettings({
        _id: updatedManager.userId,
        accessMode: MissionManagerAccessMode.RoleView,
        excludeFromTeamPulseEmails: !updatedManager.teamPulse,
        excludeFromInvoiceEmails: !updatedManager.invoices,
        excludeFromBuilderFeedbackEmails: !updatedManager.builderFeedback,
        excludeFromMissionUpdatesEmails: !updatedManager.missionUpdates,
      });
    }
  }, [formState, collaborators, loading, didFormStateChange]);

  const isMissionInvite = useMemo(() => !!missionSpec, [missionSpec]);

  const managersNotInCollaborators = useMemo(() => {
    if (!currentAccountId) {
      return [];
    }
    return managers.filter(
      (manager) =>
        manager?.user?.uid &&
        !(collaborators || []).find((c) => c?.user?.id === manager?.user?.uid)
    );
  }, [collaborators, managers, currentAccountId]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      const direction = prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const handleEmailSettingsChange = (
    collaborator: AccountMember,
    emailSettings: string[]
  ) => {
    const newFormState = [...formState];
    const state = newFormState.find((s) => s.userId === collaborator.user.id);
    if (!state) {
      return;
    }
    const newState = { ...state };
    newState.invoices = emailSettings.includes(EmailSettings.Invoices);
    newState.teamPulse = emailSettings.includes(EmailSettings.TeamPulse);
    newState.builderFeedback = emailSettings.includes(
      EmailSettings.BuilderFeedback
    );
    newState.missionUpdates = emailSettings.includes(
      EmailSettings.MissionUpdates
    );
    const idx = newFormState.findIndex(
      (s) => s.userId === collaborator.user.id
    );
    if (idx !== -1) {
      newFormState[idx] = newState;
      !readyToSave && setReadyToSave(true);
      setFormState(newFormState);
    }
  };

  const sortByAccessLevel = (a: AccountMember, b: AccountMember): number => {
    const accessLevelWeight = (accessLevel: AccountRoleAccessLevel) => {
      switch (accessLevel) {
        case AccountRoleAccessLevel.BDAdmin:
          return 2;
        case AccountRoleAccessLevel.BDManager:
          return 1;
        default:
          return 0;
      }
    };

    // Put current user first
    if (a.user.id === currentUser?.uid) {
      return -1;
    }
    if (b.user.id === currentUser?.uid) {
      return 1;
    }

    // Sort by access level weight
    const aWeight = accessLevelWeight(a.accessLevel);
    const bWeight = accessLevelWeight(b.accessLevel);
    if (aWeight !== bWeight) {
      return sortConfig.direction === "desc"
        ? bWeight - aWeight
        : aWeight - bWeight;
    }

    // Sort by activated status
    if (a.user.activated !== b.user.activated) {
      return a.user.activated ? -1 : 1;
    }

    // If all properties are equal - no swapping needed
    return 0;
  };

  const sortByRole = (a: AccountMember, b: AccountMember): number => {
    // Roles sort order, least to most access
    const rolesInOrder = [
      Role.WorkspaceMember,
      Role.MissionMember,
      Role.MissionAdmin,
      Role.WorkspaceAdmin,
      Role.TeamAdvisor,
    ];

    // Sort by role weight
    const aRole = getPrimaryRole(a.roles);
    const bRole = getPrimaryRole(b.roles);
    const aWeight = aRole ? rolesInOrder.indexOf(aRole) : -1;
    const bWeight = bRole ? rolesInOrder.indexOf(bRole) : -1;
    if (aWeight !== bWeight) {
      return sortConfig.direction === "desc"
        ? bWeight - aWeight
        : aWeight - bWeight;
    }

    return 0;
  };

  const filteredCollaborators = useMemo(() => {
    if (!collaborators) {
      return [];
    }
    if (!clientRolesAndPermissionsEnabled) {
      return collaborators;
    }

    return collaborators.filter((collaborator) => {
      const filteredRoles = isMissionInvite
        ? missionReadRoles
        : workspaceReadRoles;

      const roles = getMissionSpecificRoles(
        collaborator.roles || [],
        missionSpec?.id
      );
      return roles.some((role) => filteredRoles.includes(role));
    });
  }, [
    collaborators,
    clientRolesAndPermissionsEnabled,
    isMissionInvite,
    missionSpec?.id,
  ]);

  const sortedCollaborators = useMemo(() => {
    if (!filteredCollaborators) {
      return [];
    }
    if (sortConfig.key === "role") {
      const sorter = clientRolesAndPermissionsEnabled
        ? sortByRole
        : sortByAccessLevel;
      return [...filteredCollaborators].sort(sorter);
    } else if (sortConfig.key === "collaborator") {
      return [...filteredCollaborators].sort((a, b) => {
        return sortConfig.direction === "asc"
          ? a.user.fullName.localeCompare(b.user.fullName)
          : b.user.fullName.localeCompare(a.user.fullName);
      });
    }
    return filteredCollaborators;
  }, [filteredCollaborators, sortConfig]);

  const isDesktop = useMinBreakpoint("sm");

  if (loading) {
    return <LoadingFallback height={300} />;
  }

  if ((!loading && error) || !collaborators) {
    return <p>There was an error: Refresh the page</p>;
  }

  if (isDesktop) {
    return (
      <div css={styles.container}>
        <>
          {currentUser?.isAdmin && managersNotInCollaborators.length > 0 && (
            <div css={styles.managerWarning}>
              <Typography variant="textMedium" weight="regular" component="p">
                Mission managers not in workspace:{" "}
                {managersNotInCollaborators
                  .map((m) => m.user?.email)
                  .join(", ")}
              </Typography>
              <Typography variant="textMedium" weight="regular" component="p">
                Please add these user email addresses with the correct settings,
                or contact a system admin to get them removed.
              </Typography>
            </div>
          )}

          <div css={styles.table}>
            <>
              <TableCellHeader
                saving={saving}
                name={`Collaborators (${sortedCollaborators.length})`}
                style={tableHeaderStyles}
                sortable
                sorted={sortConfig.key === "collaborator"}
                onSort={() => handleSort("collaborator")}
                sortConfig={sortConfig}
              />
              <TableCellHeader
                name="Role"
                style={tableHeaderStyles}
                sortable
                sorted={sortConfig.key === "role"}
                onSort={() => handleSort("role")}
                sortConfig={sortConfig}
              />

              {showAdminSettings && !clientRolesAndPermissionsEnabled && (
                <>
                  <TableCellHeader
                    name="Team Pulse ✉️"
                    style={tableHeaderStyles}
                  />
                  <TableCellHeader
                    name="Invoices ✉️"
                    style={tableHeaderStyles}
                  />
                  <TableCellHeader
                    name="Main manager"
                    style={tableHeaderStyles}
                  />
                </>
              )}
              {showAdminSettings && clientRolesAndPermissionsEnabled && (
                <>
                  <TableCellHeader
                    name="Hiring manager"
                    style={centeredTableHeader}
                    tooltip={{
                      text: "Hiring managers will get regular updates on team health.",
                      zIndex: 10001,
                      children: (
                        <span
                          style={{
                            position: "relative",
                            top: 3,
                            marginLeft: 2,
                          }}
                        >
                          <Icon size="md" name="details" />
                        </span>
                      ),
                    }}
                  />
                </>
              )}
              {showEmailSettings && (
                <TableCellHeader
                  name="Email settings"
                  style={tableHeaderStyles}
                />
              )}
              <div css={css(styles.tableCell, styles.tableHeaderCell)}>
                <Typography
                  variant="textSmall"
                  weight="regular"
                  component="div"
                >
                  {clientRolesAndPermissionsEnabled ? <>&nbsp;</> : "Action(s)"}
                </Typography>
              </div>

              <CollaboratorRows
                collaborators={sortedCollaborators}
                managers={managers}
                showAdminSettings={showAdminSettings}
                showEmailSettings={showEmailSettings}
                formState={formState}
                updateMissionSettings={updateMissionSettings}
                handleEmailSettingsChange={handleEmailSettingsChange}
                currentUser={currentUser}
                saving={!!saving}
                style={styles.tableCell}
                closeCollaboratorManageModal={
                  props.closeCollaboratorManageModal
                }
              />
            </>
          </div>
        </>
      </div>
    );
  }

  return (
    <div css={[styles.container, styles.cards]}>
      {sortedCollaborators.map((collaborator, index) => {
        // We don't want to send the builder feedback or mission updates to billing contacts
        const isUserBillingContact = isBillingContact({
          email: collaborator.user.email,
          firstName: collaborator.user.firstName,
          lastName: collaborator.user.lastName,
        });

        // Create proper email settings for collaborator
        const state = formState.find((s) => s.userId === collaborator.user.id);
        const emailSettings: EmailSettings[] = [];
        if (state?.invoices) {
          emailSettings.push(EmailSettings.Invoices);
        }
        if (state?.teamPulse) {
          emailSettings.push(EmailSettings.TeamPulse);
        }

        if (!isUserBillingContact && state?.builderFeedback) {
          emailSettings.push(EmailSettings.BuilderFeedback);
        }
        if (!isUserBillingContact && state?.missionUpdates) {
          emailSettings.push(EmailSettings.MissionUpdates);
        }

        return (
          <CollaboratorCard
            missionSpecId={missionSpec?.id}
            showHiringManagerCheckbox={showAdminSettings}
            allowEmailSettingsEdit={
              showAdminSettings || collaborator.user.id === currentUser?.uid
            }
            key={`collaborator--card--${index}`}
            collaborator={collaborator}
            onHiringManagerChange={(checked) => {
              updateMemberAttributes({
                userId: collaborator.user.id,
                attributes: {
                  hiringManager: checked,
                },
                prevState: collaborators,
              });
            }}
            saving={saving}
            emailSettings={emailSettings}
            onEmailSettingsChange={(emailSettings) =>
              handleEmailSettingsChange(collaborator, emailSettings)
            }
            closeCollaboratorManageModal={props.closeCollaboratorManageModal}
          />
        );
      })}
    </div>
  );
};

export default observer(CollaboratorsTable);
